/**
 * Turnstile — Cloudflare Turnstile CAPTCHA widget for Supabase auth.
 *
 * Exposes ref with .reset() method. After each API call that consumes the
 * token, call reset() to trigger a fresh challenge. The new token arrives
 * via onToken callback.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileProps {
  onToken: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  className?: string;
}

export function isTurnstileEnabled(): boolean {
  return !!SITE_KEY;
}

let scriptLoaded = false;

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(
  function Turnstile({ onToken, onExpire, onError, className }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const startRef = useRef(performance.now());
    const onTokenRef = useRef(onToken);
    const onExpireRef = useRef(onExpire);
    const onErrorRef = useRef(onError);
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;

    function renderWidget() {
      if (!containerRef.current || !window.turnstile || !SITE_KEY) {
        if (performance.now() - startRef.current < 10000) {
          setTimeout(renderWidget, 300);
        } else {
          onErrorRef.current?.();
        }
        return;
      }
      if (widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => {
          onTokenRef.current(token);
        },
        "expired-callback": () => onExpireRef.current?.(),
        "error-callback": () => onErrorRef.current?.(),
        theme: "auto",
        size: "flexible",
        appearance: "always",
        execution: "render",
      });
    }

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      if (!SITE_KEY) return;
      startRef.current = performance.now();
      if (window.turnstile) {
        renderWidget();
        return () => {
          if (widgetIdRef.current && window.turnstile) {
            window.turnstile.remove(widgetIdRef.current);
          }
        };
      }
      if (!scriptLoaded) {
        scriptLoaded = true;
        window.onTurnstileLoad = () => renderWidget();
        const script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad";
        script.async = true;
        script.crossOrigin = "anonymous";
        script.referrerPolicy = "no-referrer";
        script.onerror = () => onErrorRef.current?.();
        document.head.appendChild(script);
      } else {
        window.onTurnstileLoad = () => renderWidget();
        renderWidget();
      }
      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      };
    }, []);

    if (!SITE_KEY) return null;
    return <div ref={containerRef} className={className} />;
  },
);
